import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, User, Phone, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useParticipant } from "@/context/ParticipantContext";
import { LOCATIONS, getBranchesForProvince } from "@/data/locations";
import { REGISTRATION } from "@/constants/testIds";
import { formatApiErrorDetail } from "@/lib/api";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_60dfbe01-e8f1-47bb-9c72-2a0d72443632/artifacts/waarlbny_DENSO-main-logo-web.jpg";

const BG_IMAGE_URL = "https://images.unsplash.com/photo-1572282431238-bd1d44df8550";

export default function Registration() {
  const navigate = useNavigate();
  const { register } = useParticipant();
  const [province, setProvince] = useState("");
  const [branch, setBranch] = useState("");
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [mobile, setMobile] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const branches = useMemo(() => getBranchesForProvince(province), [province]);

  const handleProvinceChange = (val) => {
    setProvince(val);
    setBranch("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!province || !branch || !name.trim() || !surname.trim() || !mobile.trim()) {
      setError("Please complete all fields before continuing.");
      return;
    }
    if (!/^[0-9+\s]{7,15}$/.test(mobile.trim())) {
      setError("Please enter a valid mobile number.");
      return;
    }
    setSubmitting(true);
    try {
      await register({
        name: name.trim(),
        surname: surname.trim(),
        mobile: mobile.trim(),
        province,
        branch,
      });
      navigate("/home");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen relative flex items-center justify-center p-4 sm:p-6"
      style={{ backgroundImage: `url(${BG_IMAGE_URL})`, backgroundSize: "cover", backgroundPosition: "center" }}
    >
      <div className="absolute inset-0 bg-slate-950/80" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden"
      >
        <div className="bg-denso-red px-6 py-6 text-center">
          <img src={LOGO_URL} alt="DENSO" className="h-12 mx-auto rounded-sm mb-3" />
          <h1 className="text-white font-heading font-bold text-xl">Product Training Platform</h1>
          <p className="text-white/80 text-sm mt-1">Store Locator & Registration</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-5">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" /> Province
            </Label>
            <Select value={province} onValueChange={handleProvinceChange}>
              <SelectTrigger data-testid={REGISTRATION.provinceTrigger} className="w-full">
                <SelectValue placeholder="Select your province" />
              </SelectTrigger>
              <SelectContent>
                {LOCATIONS.map((l) => (
                  <SelectItem key={l.province} value={l.province}>
                    {l.province}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" /> Autozone Branch
            </Label>
            <Select value={branch} onValueChange={setBranch} disabled={!province}>
              <SelectTrigger data-testid={REGISTRATION.branchTrigger} className="w-full">
                <SelectValue placeholder={province ? "Select your branch" : "Select a province first"} />
              </SelectTrigger>
              <SelectContent>
                {branches.map((b) => (
                  <SelectItem key={b} value={b}>
                    {b}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
                <User className="h-3.5 w-3.5" /> Name
              </Label>
              <Input
                data-testid={REGISTRATION.nameInput}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="First name"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Surname</Label>
              <Input
                data-testid={REGISTRATION.surnameInput}
                value={surname}
                onChange={(e) => setSurname(e.target.value)}
                placeholder="Surname"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
              <Phone className="h-3.5 w-3.5" /> Mobile Number
            </Label>
            <Input
              data-testid={REGISTRATION.mobileInput}
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="082 123 4567"
            />
          </div>

          {error && (
            <p data-testid={REGISTRATION.errorMessage} className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">
              {error}
            </p>
          )}

          <Button
            type="submit"
            data-testid={REGISTRATION.continueButton}
            disabled={submitting}
            className="w-full bg-denso-red hover:bg-denso-lightred text-white h-11 font-semibold transition-all duration-200 hover:-translate-y-0.5"
          >
            {submitting ? "Please wait..." : "Continue"}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </form>
      </motion.div>
    </div>
  );
}
